// frontend/src/components/revit/UploadRevitPlugin.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Code,
  Divider,
  FileInput,
  Group,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { MEDIA_ASSET_KEYS } from "@multiconsult-group/multiplacer-core";
import { getApiBaseUrl } from "../utils";

type UploadResult = {
  uploaded?: {
    blobName?: string;
    contentType?: string;
    sizeBytes?: number;
    originalFileName?: string;
  };
  mediaAsset?: any;
  message?: string;
};

// Debug: simuler treg opplasting (5s) for å se progressbaren
const DEBUG_SIMULATE_5S_UPLOAD = false;

export const UploadRevitPlugin: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);

  const [title, setTitle] = useState("Multiplacer Revit-plugin");
  const [version, setVersion] = useState("");
  const [description, setDescription] = useState("");

  const [filenameOverride, setFilenameOverride] = useState("");

  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<number>(0);

  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);

  // Runtime debug-toggle (så du slipper recompile)
  const [debugSimulate5s, setDebugSimulate5s] = useState<boolean>(DEBUG_SIMULATE_5S_UPLOAD);

  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const effectiveFilename = useMemo(() => {
    const override = filenameOverride.trim();
    if (override.length > 0) return override;
    return file?.name ?? "";
  }, [filenameOverride, file]);

  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

  const handleUpload = async () => {
    setError(null);
    setResult(null);

    if (!file) {
      setError("Velg en fil først.");
      return;
    }

    const formData = new FormData();

    // ⚠️ Viktig: backend uploadSingleFileToBlob pleier å lese et "file"-felt.
    // Hvis dere bruker annet feltnavn i multer, bytt "file" her.
    formData.append("file", file);

    // Metadata som backend leser fra req.body
    formData.append("title", title);
    if (version.trim()) formData.append("version", version.trim());
    if (description.trim()) formData.append("description", description.trim());
    if (effectiveFilename.trim()) formData.append("filename", effectiveFilename.trim());

    const key = MEDIA_ASSET_KEYS.REVIT_PLUGIN_INSTALLER;
    const url = `${getApiBaseUrl()}/api/multiplacer/media-assets/${key}/upload`;

    try {
      setIsUploading(true);
      setProgress(0);

      // Debug: fake progresjon i 5 sekunder
      if (debugSimulate5s) {
        const steps = 50;
        for (let i = 1; i <= steps; i += 1) {
          setProgress(Math.round((i / steps) * 100));
          await sleep(5000 / steps);
        }
      }

      const json = await uploadWithProgress(url, formData, (p) => {
        // Hvis debugSimulate5s er på, viser vi fortsatt ekte progresjon *etter* simulasjonen,
        // men vi lar den ikke gå bakover.
        setProgress((prev) => Math.max(prev, p));
      });

      setResult(json);
    } catch (e: any) {
      setError(e?.message ?? "Ukjent feil ved opplasting.");
    } finally {
      setIsUploading(false);
      xhrRef.current = null;
    }
  };

  const handleCancel = () => {
    try {
      xhrRef.current?.abort();
      setError("Opplasting avbrutt.");
    } finally {
      setIsUploading(false);
    }
  };

  const uploadWithProgress = (
    url: string,
    formData: FormData,
    onProgress: (percent: number) => void
  ): Promise<UploadResult> => {
    return new Promise<UploadResult>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      xhr.open("POST", url, true);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const percent = Math.round((evt.loaded / evt.total) * 100);
        onProgress(percent);
      };

      xhr.onload = () => {
        try {
          const text = xhr.responseText || "{}";
          const parsed = JSON.parse(text) as UploadResult;

          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress(100);
            resolve(parsed);
            return;
          }

          reject(new Error(parsed?.message ?? `Upload failed (${xhr.status})`));
        } catch (err: any) {
          reject(new Error(err?.message ?? "Kunne ikke lese svar fra server."));
        }
      };

      xhr.onerror = () => reject(new Error("Nettverksfeil under opplasting."));
      xhr.onabort = () => reject(new Error("Opplasting avbrutt."));

      xhr.send(formData);
    });
  };

  return (
    <Paper withBorder p="md">
      {/* Scrollbart innhold (for eksempel inni en side med faste høyder) */}
      <ScrollArea type="auto" offsetScrollbars>
        <Stack gap="sm">
          <Text fw={600}>Last opp ny versjon</Text>

          <Alert variant="light" title="Tips" color="gray">
            Sett <b>visibility</b> til <b>AUTH</b> hvis dere vil at kun innloggede skal kunne laste
            ned.
          </Alert>

          <FileInput
            label="Installasjonsfil"
            placeholder="Velg .msi / .exe / .zip"
            value={file}
            onChange={setFile}
            clearable
          />

          <Group grow>
            <TextInput
              label="Tittel"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <TextInput
              label="Versjon"
              placeholder="f.eks. 1.4.2"
              value={version}
              onChange={(e) => setVersion(e.currentTarget.value)}
            />
          </Group>

          <Textarea
            label="Beskrivelse"
            placeholder="Hva er nytt i denne versjonen?"
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />

          <TextInput
            label="Filnavn (valgfritt)"
            description="Hvis du vil at nedlastingen skal få et annet navn enn originalfilen."
            value={filenameOverride}
            onChange={(e) => setFilenameOverride(e.currentTarget.value)}
          />

          <Divider />

          <Stack gap="xs">
            <Group justify="space-between">
              <Group gap="xs">
                {isUploading && (
                  <Button variant="light" onClick={handleCancel}>
                    Avbryt
                  </Button>
                )}
                <Button
                  onClick={handleUpload}
                  loading={isUploading}
                  disabled={!file || isUploading}
                >
                  Last opp
                </Button>
              </Group>
            </Group>

            {(isUploading || progress > 0) && <Progress value={progress} animated striped />}
          </Stack>

          {error && (
            <Alert color="red" title="Feil">
              {error}
            </Alert>
          )}

          {result && (
            <Alert color="green" title="Opplasting fullført">
              <Text size="sm">Serveren svarte:</Text>
              <Code block>{JSON.stringify(result, null, 2)}</Code>
            </Alert>
          )}
        </Stack>
      </ScrollArea>
    </Paper>
  );
};
